import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, User, Search, Compass, Package, MessageSquare, Home, ShoppingCart, ChevronRight, ChevronDown, Palette, BookOpen, HelpCircle, Sparkles, Heart, Award } from 'lucide-react';
import WishlistButton from './WishlistButton';
import CurrencyToggle from './CurrencyToggle';
import SoundToggle from './SoundToggle';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";
const API = `${window.location.origin}/api`;

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const { cartCount } = useCart();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  const isAdmin = location.pathname.startsWith('/admin');

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [location.pathname]);

  // Click outside to close search
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Instant search with debounce
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(() => {
      axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}&limit=6`)
        .then(res => { setSearchResults(res.data); setSearchLoading(false); })
        .catch(() => setSearchLoading(false));
    }, 250);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  const menuLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/shop', label: 'Shop', icon: ShoppingCart },
    { to: '/scent-finder', label: 'Scent Finder', icon: Compass },
    { to: '/bundle', label: 'Bundle & Save', icon: Package },
    { to: '/custom-scent', label: 'Create Your Scent', icon: Palette },
    { to: '/ambassador-apply', label: 'Become an Ambassador', icon: Award },
    { to: '/blog', label: 'Blog', icon: BookOpen },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/track-order', label: 'Track Order', icon: Package },
    { to: '/reviews', label: 'Reviews', icon: MessageSquare },
    { to: '/after-care', label: 'After Care', icon: Sparkles },
    { to: '/about', label: 'About / Contact', icon: Heart },
  ];

  const suggestions = ['Fresh', 'Sweet', 'Woody', 'Date Night', 'Office', 'Dior Sauvage'];

  if (isAdmin) return null;

  return (
    <>
      <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-[#1C7D3F]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
              <img src={LOGO_URL} alt="JS Fragrance" className="h-10 sm:h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              <Link to="/" className={`gold-underline text-sm uppercase tracking-[0.2em] transition-colors ${location.pathname === '/' ? 'text-[#D4AF37]' : 'text-white/70 hover:text-white'}`}>Home</Link>
              <ShopDropdown />
              <Link to="/scent-finder" className={`gold-underline text-sm uppercase tracking-[0.2em] transition-colors ${location.pathname === '/scent-finder' ? 'text-[#D4AF37]' : 'text-white/70 hover:text-white'}`}>Scent Finder</Link>
              <Link to="/custom-scent" className={`gold-underline text-sm uppercase tracking-[0.2em] transition-colors ${location.pathname === '/custom-scent' ? 'text-[#D4AF37]' : 'text-white/70 hover:text-white'}`}>Create Scent</Link>
            </nav>

            {/* Right side icons */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Currency Toggle — hidden on small screens */}
              <div className="hidden sm:block">
                <CurrencyToggle compact />
              </div>

              {/* Sound toggle — always visible (sound is a brand feature) */}
              <SoundToggle />

              {/* Search */}
              <button
                onClick={() => { setSearchOpen(!searchOpen); setMenuOpen(false); }}
                className="text-white/80 hover:text-[#D4AF37] transition-colors"
                data-testid="header-search-button"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Wishlist */}
              <WishlistButton />

              {/* Cart */}
              <Link to="/cart" className="relative" data-testid="header-cart-button">
                <ShoppingBag className="w-5 h-5 text-white/80 hover:text-[#D4AF37] transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs w-5 h-5 flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Hamburger Menu */}
              <button
                onClick={() => { setMenuOpen(!menuOpen); setSearchOpen(false); }}
                className="text-white/80 hover:text-[#D4AF37] transition-colors"
                data-testid="hamburger-menu-button"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Dropdown */}
        {searchOpen && (
          <div ref={searchRef} className="absolute top-full left-0 right-0 bg-[#0A0A0A] border-b border-white/10 z-50" data-testid="search-dropdown">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, scent, or occasion..."
                  className="w-full bg-white/5 border border-white/10 rounded-none pl-12 pr-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50 text-sm font-sans"
                  autoFocus
                  data-testid="search-input"
                />
              </div>

              {/* Suggestions */}
              {searchQuery.length < 2 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-white/30 text-xs font-sans">Try:</span>
                  {suggestions.map(s => (
                    <button
                      key={s}
                      onClick={() => setSearchQuery(s)}
                      className="text-xs px-3 py-1 border border-white/10 text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-colors font-sans"
                      data-testid={`search-suggestion-${s.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Results */}
              {searchLoading && <p className="text-white/30 text-sm mt-3 font-sans">Searching...</p>}
              {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="text-white/30 text-sm mt-3 font-sans">No results for "{searchQuery}"</p>
              )}
              {searchResults.length > 0 && (
                <div className="mt-3 divide-y divide-white/5" data-testid="search-results">
                  {searchResults.map(product => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      onClick={() => setSearchOpen(false)}
                      className="flex items-center gap-4 py-3 hover:bg-white/5 transition-colors px-2 group"
                      data-testid={`search-result-${product.id}`}
                    >
                      <div className="w-12 h-12 bg-[#141414] flex-shrink-0 overflow-hidden">
                        <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-sans truncate group-hover:text-[#D4AF37] transition-colors">{product.name}</p>
                        <p className="text-white/30 text-xs font-sans">
                          {product.is_signature ? 'JS Original' : (product.inspired_by ? `Inspired by ${product.inspired_by}` : product.gender)} · ₱{product.base_price?.toLocaleString()}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
                    </Link>
                  ))}
                  <Link
                    to={`/shop?search=${encodeURIComponent(searchQuery)}`}
                    onClick={() => setSearchOpen(false)}
                    className="block text-center py-3 text-[#D4AF37] text-sm font-sans hover:underline"
                  >
                    View all results
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Slide-out Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40" data-testid="menu-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute top-0 right-0 w-72 sm:w-80 h-full bg-[#0A0A0A] border-l border-white/10 overflow-y-auto animate-slide-in-right">
            <div className="p-6 pt-20">
              {/* User Profile */}
              {isAuthenticated ? (
                <Link to="/account" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 mb-8 group" data-testid="menu-account">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                  )}
                  <div>
                    <p className="text-white text-sm font-sans">{user?.name || 'Customer'}</p>
                    <p className="text-white/30 text-xs font-sans">View Account</p>
                  </div>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 mb-8 px-4 py-3 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors" data-testid="menu-login">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] font-sans">Login / Register</span>
                </Link>
              )}

              {/* Ambassador-only quick link */}
              {isAuthenticated && user?.role === 'ambassador' && (
                <Link to="/ambassador" onClick={() => setMenuOpen(false)}
                  data-testid="menu-ambassador"
                  className="flex items-center justify-between px-4 py-3 mb-3 bg-[#D4AF37]/5 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#D4AF37] font-sans font-medium">Ambassador Dashboard</span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37]" />
                </Link>
              )}

              {/* Navigation Links */}
              <nav className="space-y-1">
                {menuLinks
                  .filter(link => {
                    // Hide "Become an Ambassador" CTA if user is already an
                    // ambassador or admin — they shouldn't be invited to apply
                    // again. Also avoids the visual conflict with the
                    // dedicated "Ambassador Dashboard" link above.
                    if (link.to === '/ambassador-apply' && isAuthenticated &&
                        (user?.role === 'ambassador' || user?.role === 'admin' || user?.role === 'super_admin')) {
                      return false;
                    }
                    return true;
                  })
                  .map(link => {
                  const Icon = link.icon;
                  const isActive = location.pathname === link.to;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-4 px-4 py-3 transition-colors ${
                        isActive ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`menu-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm uppercase tracking-[0.15em] font-sans">{link.label}</span>
                      <ChevronRight className="w-4 h-4 ml-auto opacity-30" />
                    </Link>
                  );
                })}
              </nav>

              {/* Divider */}
              <div className="border-t border-white/5 my-6" />

              {/* Secondary Links */}
              <nav className="space-y-1">
                {[
                  { to: '/about', label: 'About Us' },
                  { to: '/shipping-policy', label: 'Shipping Policy' },
                  { to: '/refund-policy', label: 'Returns & Refunds' },
                  { to: '/privacy', label: 'Privacy Policy' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-white/40 hover:text-white/70 transition-colors font-sans"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Currency switcher — mobile-friendly inside menu */}
              <div className="sm:hidden px-4 pt-4 mt-4 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-sans mb-2">Display Currency</p>
                <CurrencyToggle />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function ShopDropdown() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const ref = useRef(null);
  const onShopRoute = location.pathname.startsWith('/shop');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/category-templates`).catch(() => ({ data: { templates: [] } })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    ]).then(([tplRes, catsRes]) => {
      setTemplates(tplRes.data?.templates || []);
      setProductCategories(catsRes.data || []);
    });
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Only show categories that actually have products in the catalog
  const liveCategories = productCategories.map(c => (c || '').toLowerCase().trim());
  const visibleTemplates = templates.filter(t => {
    const matches = t.match_categories || [t.label];
    return matches.some(m => liveCategories.includes((m || '').toLowerCase().trim()));
  });

  return (
    <div ref={ref} className="relative" data-testid="header-shop-dropdown">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`gold-underline text-sm uppercase tracking-[0.2em] transition-colors flex items-center gap-1 ${onShopRoute ? 'text-[#D4AF37]' : 'text-white/70 hover:text-white'}`}
        data-testid="header-shop-trigger"
      >
        Shop {visibleTemplates.length > 0 && <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && visibleTemplates.length > 0 && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[220px] bg-[#0F0F0F] border border-[#D4AF37]/30 shadow-xl py-2 animate-fade-in-up" data-testid="header-shop-menu">
          <Link
            to="/shop"
            onClick={() => setOpen(false)}
            className="block px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-white/80 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors"
          >
            All categories
          </Link>
          <div className="h-px bg-white/8 my-1.5" />
          {visibleTemplates.map(t => (
            <Link
              key={t.slug}
              to={`/shop/${t.slug}`}
              onClick={() => setOpen(false)}
              className="block px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-white/80 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors"
              data-testid={`header-shop-${t.slug}`}
            >
              {t.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
